import React, { Component } from 'react';

import {
  Button,
  FontAwesomeIcon,
  Text,
  Flexbox,
  ClickableComponent,
} from '@schoolinks/ui-components';
import cn from 'classnames';
import { noop } from 'lodash';
import { shape, bool, func, string, number } from 'prop-types';
import { connect } from 'react-redux';
import { withState } from 'recompose';
import { bindActionCreators } from 'redux';

import { faScreenUsers } from '@fortawesome/pro-regular-svg-icons/faScreenUsers';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import removeJwt from 'actions/removeJwt';

import { isElementaryPage } from 'components/Common/LayoutV2/utils';
import * as modalActions from 'components/Modals/actions';
import withRouter from 'containers/withRouter';

import withUserType from 'containers/withUserType';
import withMediaInfo from 'HOC/withMediaInfo';
import { RouterPropTypes } from 'propTypes/RouterPropTypes';

import * as actions from 'modules/StudentDemoAccount/actions';

import s from './StudentDemoAccount.css';

@withUserType
@withState('modalOpen', 'setModalOpen', false)
@withState('stage', 'setStage', undefined)
@withState('busy', 'setBusy', false)
@withRouter
@withMediaInfo()
@connect(
  ({ studentDemoAccount, user }) => ({
    ...studentDemoAccount,
    studentPreviewCounselor: user.studentPreviewCounselor,
    userId: user.userId,
    currentUserPicture: user.profilePic,
  }),
  dispatch => bindActionCreators({ ...actions, ...modalActions }, dispatch),
)
class StudentDemoAccount extends Component {
  componentDidUpdate(prevProps) {
    const {
      modalOpen,
      setStage,
      stage,
      user: { isCounselor, isDemoStudent },
      userId,
      showModal,
      router: {
        location: { pathname },
      },
    } = this.props;
    const { stage: prevStage } = prevProps;
    if (stage && stage !== prevStage) {
      const isElementary = isElementaryPage(pathname);

      const modalProps = {
        open: modalOpen,
        onClose: () => this.onClose(),
        stage,
        isElementary,
        setStage,
        isCounselor,
        currentStudentUserPk: isDemoStudent ? userId : undefined,
      };
      showModal('STUDENT_DEMO_ACCOUNT', modalProps);
    }
  }

  onClose = () => {
    const {
      setModalOpen,
      user: { isDemoStudent },
      userId,
      resetStudentInfo,
      setStage,
    } = this.props;
    setStage();
    if (isDemoStudent) {
      resetStudentInfo(isDemoStudent, userId);
    }
    setModalOpen(false);
  };

  onExitDemo = async () => {
    const {
      switchToCounselorAccount,
      setBusy,
      router: {
        location: { pathname },
      },
    } = this.props;
    setBusy(true);
    await switchToCounselorAccount();

    const isElementary = isElementaryPage(pathname);
    let redirectUrl = '/';

    if (isElementary) {
      redirectUrl = '/el/dashboard';
    }

    removeJwt(false);

    // Clear local storage after exiting student demo
    localStorage.removeItem('studentPreviewCounselor');

    window.location = redirectUrl;
  };

  onDemoClick = async () => {
    const {
      setStage,
      user: { isCounselor },
      userId,
      updateStudentInfo,
    } = this.props;
    if (isCounselor === true) {
      setStage('select');
    } else {
      updateStudentInfo({ field: 'pk', value: userId });
      setStage('info');
    }
  };

  render() {
    const {
      disabled,
      user: { isCounselor, isDemoStudent },
      mediaInfo: { isDesktop },
      k12AdminTooltip,
    } = this.props;

    if (!isDesktop) {
      return (
        <ClickableComponent
          className={s.menuItem}
          disabled={disabled}
          onClick={isDemoStudent ? this.onExitDemo : this.onDemoClick}
        >
          <Flexbox alignItems="center" gap={1}>
            <FontAwesomeIcon icon={faScreenUsers} />
            <Text variant='bodySm400'>
              {isDemoStudent ? 'Exit Student Demo' : 'Student Demo'}
            </Text>
            {isDemoStudent && (
              <FontAwesomeIcon color="inverted" icon={faXmark} />
            )}
          </Flexbox>
        </ClickableComponent>
      );
    }

    return (
      <Button
        className={cn({
          [s.demoButton]: isDemoStudent,
          [s.button]: !isDemoStudent,
        })}
        disabled={disabled}
        color="secondary"
        variant={isDemoStudent ? 'filled' : 'outlined'}
        label={
          isDemoStudent ? (
            <Flexbox alignItems="center" gap={1}>
              <Text color="inverted" variant='bodySm600'>
                Exit Student Demo
              </Text>
              <FontAwesomeIcon color="inverted" icon={faXmark} />
            </Flexbox>
          ) : (
            'Student Demo'
          )
        }
        hasIconLeft
        icon={
          <FontAwesomeIcon
            icon={faScreenUsers}
            color={isDemoStudent ? 'inverted' : 'default'}
          />
        }
        onClick={isDemoStudent ? this.onExitDemo : this.onDemoClick}
        hasWhiteBackground={false}
        tooltipProps={{
          title: isCounselor ? k12AdminTooltip : '',
        }}
      />
    );
  }
}

StudentDemoAccount.propTypes = {
  user: shape({
    isCounselor: bool,
    isStudent: bool,
    isDemoStudent: bool,
  }),
  modalOpen: bool,
  setModalOpen: func,
  stage: string,
  setStage: func,
  updateStudentInfo: func,
  switchToCounselorAccount: func,
  resetStudentInfo: func,
  userId: number,
  showModal: func,
  disabled: bool,
  setBusy: func,
  router: RouterPropTypes,
  k12AdminTooltip: string,
  mediaInfo: shape({
    isMobile: bool,
  }).isRequired,
};

StudentDemoAccount.defaultProps = {
  user: {},
  modalOpen: false,
  setModalOpen: noop,
  stage: null,
  setStage: noop,
  updateStudentInfo: noop,
  switchToCounselorAccount: noop,
  resetStudentInfo: noop,
  userId: null,
  showModal: noop,
  disabled: false,
  setBusy: noop,
  router: {},
  k12AdminTooltip: null,
};

export default StudentDemoAccount;
